import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { BodyM, HeadlineXL } from '@sorare/core/src/atoms/typography';
import { ImprovementChip } from '@sorare/marketplace/src/atoms/ImprovementChip';
import { CardsShow } from '@sorare/marketplace/src/components/card/CardsShow';

import type {
  LevelUpModal_anyCard,
  LevelUpModal_cardCollection,
  LevelUpModal_userCardCollection,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  padding: var(--double-unit);
`;
const ClubIcon = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
`;

type Props = {
  cards: LevelUpModal_anyCard[];
  cardCollection: Nullable<LevelUpModal_cardCollection>;
  userCardCollection: Nullable<LevelUpModal_userCardCollection>;
};

export const LevelUpModal = ({
  cards,
  cardCollection,
  userCardCollection,
}: Props) => {
  const currentScore = userCardCollection?.score || 0;
  const bonusThresholds = [...(cardCollection?.bonusThresholds || [])].sort(
    (a, b) => {
      return b.threshold - a.threshold;
    }
  );
  const previousThreshold = bonusThresholds.find(
    threshold => threshold.threshold < currentScore
  );
  const nextThreshold = bonusThresholds
    .reverse()
    .find(threshold => threshold.threshold > currentScore);

  if (!nextThreshold || !cards?.length) return null;

  return (
    <Root center className="text-center" gap={2}>
      <Vertical>
        <HeadlineXL>
          <FormattedMessage defaultMessage="Level up now" />
        </HeadlineXL>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage
            defaultMessage="You're about to add {count, plural, one {# card} other {# cards}} to your cart to reach the next collection bonus"
            values={{
              count: cards.length,
            }}
          />
        </BodyM>
      </Vertical>
      {!!cardCollection?.anyTeam?.pictureUrl && (
        <ClubIcon src={cardCollection.anyTeam.pictureUrl} />
      )}
      <ImprovementChip
        size="large"
        Label={v => <>+{v.value * 100}%</>}
        value={previousThreshold?.bonus || 0}
        valueIncreased={nextThreshold.bonus}
      />
      <Progress
        style={
          {
            '--height': 'var(--intermediate-unit)',
            '--progress-color':
              'linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)',
            '--progress-background-color':
              ' linear-gradient(-90deg, rgba(255, 251, 104, 0.16) 0%, rgba(255, 250, 52, 0.32) 75.96%, rgba(255, 251, 104, 0.16) 100%)',
          } as CSSProperties
        }
        min={previousThreshold?.threshold || 0}
        value={currentScore}
        max={nextThreshold.threshold}
      />
      <CardsShow cards={cards} />
    </Root>
  );
};

LevelUpModal.fragments = {
  anyCard: gql`
    fragment LevelUpModal_anyCard on AnyCardInterface {
      slug
      ...CardsShow_anyCard
    }
    ${CardsShow.fragments.anyCard}
  ` as TypedDocumentNode<LevelUpModal_anyCard>,
  cardCollection: gql`
    fragment LevelUpModal_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        pictureUrl
      }
      bonusThresholds {
        bonus
        threshold
      }
    }
  ` as TypedDocumentNode<LevelUpModal_cardCollection>,
  userCardCollection: gql`
    fragment LevelUpModal_userCardCollection on UserCardCollection {
      id
      score
    }
  ` as TypedDocumentNode<LevelUpModal_userCardCollection>,
};
